"use client";

import { InputDate } from "@admin-ui/components/base/input/input-date";
import { ComboBox } from "@admin-ui/components/base/select/combobox";
import { SelectItem } from "@admin-ui/components/base/select/select-item";
import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import type AdminAccountsBadge from "@travily/types/models/AdminAccountsBadge";
import { type Key, useEffect, useMemo, useState } from "react";
import { useFilter } from "react-aria";
import { Heading as AriaHeading, type DateValue } from "react-aria-components";

export interface BadgesAwardModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  availableBadges: AdminAccountsBadge[];
  onSubmit: (params: { badgeKey: string; level: number; awardedOn?: string }) => void;
  copy: {
    title: string;
    description: string;
    searchLabel: string;
    searchPlaceholder: string;
    levelLabel: string;
    awardedOnLabel: string;
    cancel: string;
    confirm: string;
  };
  isSubmitting?: boolean;
}

export function BadgesAwardModal({
  isOpen,
  onOpenChange,
  availableBadges,
  onSubmit,
  copy,
  isSubmitting,
}: BadgesAwardModalProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  // null = "use the default for the picked badge"; set to a number when the admin overrides.
  const [levelOverride, setLevelOverride] = useState<number | null>(null);
  // null = "let the backend decide" (founding date / now); set when the admin picks a date.
  const [awardedOn, setAwardedOn] = useState<DateValue | null>(null);

  const { contains } = useFilter({ sensitivity: "base" });

  const tiers = useMemo(() => {
    const badge = availableBadges.find((b) => b.key === selectedKey);
    return badge?.achievement_type === "count" ? (badge.tiers ?? null) : null;
  }, [availableBadges, selectedKey]);

  const level = levelOverride ?? tiers?.[0] ?? 1;

  useEffect(() => {
    if (!isOpen) {
      setSelectedKey(null);
      setInputValue("");
      setLevelOverride(null);
      setAwardedOn(null);
    }
  }, [isOpen]);

  const items = useMemo(
    () =>
      availableBadges
        .map((badge) => ({
          id: badge.key,
          label: badge.name,
          supportingText: `${badge.category} · ${badge.achievement_type}`,
        }))
        .filter((item) => contains(item.label, inputValue) || contains(item.supportingText, inputValue)),
    [availableBadges, contains, inputValue],
  );

  const handleConfirm = () => {
    if (!selectedKey) return;
    onSubmit({ badgeKey: selectedKey, level, awardedOn: awardedOn?.toString() });
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog>
          {({ close }) => (
            <div className="pointer-events-auto relative w-full overflow-visible rounded-2xl bg-primary shadow-xl sm:max-w-120">
              <CloseButton
                onPress={close}
                theme="light"
                size="sm"
                className="absolute top-3 right-3 sm:top-4 sm:right-4"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="flex flex-col gap-0.5">
                  <AriaHeading slot="title" className="text-md font-semibold text-primary">
                    {copy.title}
                  </AriaHeading>
                  <p className="text-sm text-tertiary">{copy.description}</p>
                </div>

                <ComboBox
                  label={copy.searchLabel}
                  placeholder={copy.searchPlaceholder}
                  items={items}
                  selectedKey={selectedKey}
                  inputValue={inputValue}
                  shortcut={false}
                  popoverClassName="max-h-80"
                  defaultFilter={() => true}
                  onInputChange={setInputValue}
                  onSelectionChange={(key: Key | null) => {
                    setSelectedKey(key === null ? null : String(key));
                    setLevelOverride(null);
                  }}
                >
                  {(item) => <SelectItem {...item} />}
                </ComboBox>

                {tiers && tiers.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-secondary">{copy.levelLabel}</span>
                    <div className="flex flex-wrap gap-2">
                      {tiers.map((tier) => (
                        <Button
                          key={tier}
                          size="sm"
                          color={tier === level ? "primary" : "secondary"}
                          onClick={() => setLevelOverride(tier)}
                          className="min-w-12 justify-center"
                        >
                          {tier}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <InputDate label={copy.awardedOnLabel} value={awardedOn} onChange={setAwardedOn} />
              </div>

              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="md" onClick={close}>
                  {copy.cancel}
                </Button>
                <Button
                  color="primary"
                  size="md"
                  isDisabled={!selectedKey || isSubmitting}
                  onClick={() => {
                    handleConfirm();
                    close();
                  }}
                >
                  {copy.confirm}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
